import {
    LayoutGrid,
    Wallet,
    Clock,
    Receipt,
    Sparkles,
    Wrench,
    CalendarClock,
    CalendarDays,
    Clipboard,
    UserCheck,
    FileSpreadsheet,
    Calculator,
    Lock,
} from "lucide-react";

/** Client-side mirror of backend validate_late_fine_slabs (attendance/penalty_utils.py). */
export function validateLateFineSlabs(slabs) {
    if (slabs == null) return null;
    if (!Array.isArray(slabs)) return "Late fine slabs must be a list";
    if (slabs.length === 0) return null;

    const ranges = [];
    for (let idx = 0; idx < slabs.length; idx += 1) {
        const slab = slabs[idx];
        const label = `Row ${idx + 1}`;
        const lo = Number(slab.min_minutes);
        const hi = Number(slab.max_minutes);
        if (!Number.isFinite(lo) || !Number.isFinite(hi)) {
            return `${label}: From and To must be whole minutes`;
        }
        if (lo < 0 || hi < 0) return `${label}: Minutes cannot be negative`;
        if (lo > hi) return `${label}: From cannot be greater than To`;
        const fine = Number(slab.fine ?? 0);
        const deduction = Number(slab.deduction ?? 0);
        if (!Number.isFinite(fine) || !Number.isFinite(deduction)) {
            return `${label}: Fine and deduction must be numbers`;
        }
        if (fine < 0) return `${label}: Fine cannot be negative`;
        if (deduction < 0 || deduction > 1) return `${label}: Deduction must be between 0 and 1 day`;
        ranges.push({ lo, hi, label });
    }

    ranges.sort((a, b) => a.lo - b.lo);
    if (ranges[0].lo > 1) {
        return `Gap before first slab: minutes 1–${ranges[0].lo - 1} are not covered (${ranges[0].label})`;
    }
    for (let i = 0; i < ranges.length - 1; i += 1) {
        const cur = ranges[i];
        const next = ranges[i + 1];
        if (cur.hi >= next.lo) {
            return `Late fine slabs overlap: ${cur.lo}–${cur.hi} min and ${next.lo}–${next.hi} min (${next.label})`;
        }
        if (cur.hi + 1 < next.lo) {
            return `Gap between slabs: minutes ${cur.hi + 1}–${next.lo - 1} are not covered (${next.label})`;
        }
    }
    return null;
}

export function parseConfigBool(value, defaultValue = false) {
    if (value === undefined || value === null) return defaultValue;
    if (typeof value === "boolean") return value;
    if (typeof value === "number") return value !== 0;
    if (typeof value === "string") {
        return ["true", "1", "yes"].includes(value.trim().toLowerCase());
    }
    return Boolean(value);
}

export const BRANCH_SCOPED_SECTIONS = new Set([
    "overview",
    "payroll",
    "attendance",
    "tax",
    "setup",
]);

export const COMPANY_WIDE_FORM_FIELDS = [
    "is_branch_enabled",
    "org_admin_branch_selection",
    "is_nabh",
    "modify_issued_documents",
    "loc_track_ctl",
    "birthday_alerts_enabled",
    // FR-100: company-wide because shift masters are company-level. Must stay in
    // step with COMPANY_WIDE_FIELDS in configuration/branch_config_utils.py.
    "roster_shift_management_enabled",
    // FR-104: same reason — a per-branch window would let one branch call a
    // shift dead while the other is still rostering it.
    "unused_shift_period_days",
    "unused_shift_alert_enabled",
];

export const BRANCH_SCOPED_FORM_FIELDS = [
    "freeze_payroll_config",
    "pf_limit",
    "cap_amount",
    "zero_payroll",
    "biometric_punch_only_inout",
    "is_disabled_clockin",
    "grace_times",
    "deduct_for_contract_emp",
    "is_deduction",
    "salary_deduction",
    "lop_deduction",
    "overtime_enabled",
    "overtime_multiplier",
    "overtime_rounding_minutes",
    "overtime_approval_required",
    "overtime_request_min_minutes",
    "short_duty_deduction_enabled",
    "short_duty_default_fraction",
    "short_duty_default_fine",
    "daily_grace_limit",
    "monthly_grace_limit",
    "late_penalty_enabled",
    "late_fine_slabs",
    "tds_enabled",
    "tds_default_regime",
    "block_permission_on_single_punch",
    "block_leave_in_probation",
    "lock_employee_salary_payslip",
];

export function pickFormFields(formData, fields) {
    return fields.reduce((acc, key) => {
        if (key in formData) acc[key] = formData[key];
        return acc;
    }, {});
}

export const CONFIG_SECTIONS = [
    {
        id: "overview",
        label: "Overview",
        icon: LayoutGrid,
        description: "Summary and quick access to related setup screens.",
    },
    {
        id: "payroll",
        label: "Payroll & Deductions",
        icon: Wallet,
        description: "Provident fund, LOP, overtime, and short-duty payroll rules.",
    },
    {
        id: "attendance",
        label: "Attendance & Time",
        icon: Clock,
        description: "Grace periods, late penalties, and punch restrictions.",
    },
    {
        id: "tax",
        label: "Tax & Compliance",
        icon: Receipt,
        description: "Income tax (TDS) master switch and default regime.",
    },
    {
        id: "features",
        label: "Features & Access",
        icon: Sparkles,
        description: "Branch management, org admin branch selection, location tracking, and module toggles.",
    },
    {
        id: "setup",
        label: "Operational Setup",
        icon: Wrench,
        description: "Shifts, holidays, salary templates, and other master setup.",
    },
];

export const OPERATIONAL_LINKS = [
    {
        group: "Time & Attendance",
        items: [
            { title: "Shift Configuration", description: "Define work shifts and timings", href: "/configuration/shiftconfiguration", icon: CalendarClock },
            { title: "Holiday Setup", description: "Company and branch holidays", href: "/configuration/holiday", icon: CalendarDays },
            { title: "Week Off", description: "Weekly off rules by department or role", href: "/configuration/weekoff", icon: Clipboard },
        ],
    },
    {
        group: "Payroll Structure",
        items: [
            { title: "Payroll Period", description: "Pay cycle start and cut-off dates", href: "/configuration/payrollperiod", icon: CalendarDays },
            { title: "Salary Component", description: "Earnings and deduction components", href: "/configuration/salarycomponent", icon: Wallet },
            { title: "Salary Template", description: "Department-wise salary structures", href: "/configuration/salarytemplate", icon: FileSpreadsheet },
            { title: "TDS Slab Configuration", description: "Tax rate grid by financial year", href: "/payroll/tdsconfiguration", icon: Calculator },
        ],
    },
    {
        group: "People & Security",
        items: [
            { title: "Subordinate Hierarchy", description: "Reporting structure cards", href: "/configuration/subordinate", icon: UserCheck },
            { title: "Password Policy", description: "Password rules and session timeout", href: "/admin/passwordpolicy", icon: Lock },
        ],
    },
];

export const GRACE_TIME_OPTIONS = [
    { value: 0, label: "0 minutes" },
    { value: 10, label: "10 minutes" },
    { value: 20, label: "20 minutes" },
    { value: 30, label: "30 minutes" },
];

/** Client-side mirror of backend _reject_invalid_attendance_grace. */
export function validateAttendanceGrace(formData) {
    const allowed = new Set(GRACE_TIME_OPTIONS.map((o) => o.value));
    const grace = Number(formData.grace_times);
    if (!allowed.has(grace)) {
        return "Check-in grace time must be 0, 10, 20, or 30 minutes";
    }
    for (const [field, label] of [
        ["daily_grace_limit", "Daily grace limit"],
        ["monthly_grace_limit", "Monthly grace limit"],
    ]) {
        const raw = formData[field];
        if (raw === "" || raw == null) continue;
        const value = Number(raw);
        if (!Number.isFinite(value) || !Number.isInteger(value)) {
            return `${label} must be a whole number`;
        }
        if (value < 0) return `${label} cannot be negative`;
    }
    return null;
}

export const OVERTIME_ROUNDING_OPTIONS = [
    { value: 0, label: "No rounding" },
    { value: 15, label: "15 minutes" },
    { value: 30, label: "30 minutes" },
    { value: 60, label: "60 minutes" },
];

export const TDS_REGIME_OPTIONS = [
    { value: "new", label: "New regime (115BAC) — statutory default" },
    { value: "old", label: "Old regime — opt-in with declarations" },
];

export const DEFAULT_LATE_SLABS = [
    { min_minutes: 1, max_minutes: 15, mode: "fine", fine: 50, deduction: 0 },
    { min_minutes: 16, max_minutes: 30, mode: "fine", fine: 100, deduction: 0 },
    { min_minutes: 31, max_minutes: 9999, mode: "deduction", fine: 0, deduction: 0.5 },
];

export const DEFAULT_FORM_DATA = {
    pf_limit: 15000,
    cap_amount: 1800,
    zero_payroll: true,
    modify_issued_documents: false,
    biometric_punch_only_inout: false,
    is_disabled_clockin: false,
    grace_times: 0,
    deduct_for_contract_emp: true,
    is_deduction: true,
    loc_track_ctl: false,
    is_branch_enabled: true,
    org_admin_branch_selection: true,
    is_nabh: false,
    salary_deduction: "gross",
    lop_deduction: "net_salary",
    overtime_enabled: false,
    overtime_multiplier: 2,
    overtime_rounding_minutes: 0,
    overtime_approval_required: true,
    overtime_request_min_minutes: 0,
    short_duty_deduction_enabled: false,
    short_duty_default_fraction: 0.5,
    short_duty_default_fine: 0,
    daily_grace_limit: 0,
    monthly_grace_limit: 0,
    late_penalty_enabled: false,
    late_fine_slabs: DEFAULT_LATE_SLABS,
    tds_enabled: false,
    tds_default_regime: "new",
    block_permission_on_single_punch: true,
    block_leave_in_probation: false,
    lock_employee_salary_payslip: false,
    birthday_alerts_enabled: true,
    salary_correction_next_cycle: true,
    enforce_one_payslip_per_month: true,
    allow_supplement_payroll: true,
    freeze_payroll_config: false,
    roster_shift_management_enabled: false,
    unused_shift_period_days: 90,
    unused_shift_alert_enabled: false,
};

export function mapConfigToForm(data) {
    if (!data) return { ...DEFAULT_FORM_DATA };
    return {
        pf_limit: data.pf_limit ?? DEFAULT_FORM_DATA.pf_limit,
        cap_amount: data.cap_amount ?? DEFAULT_FORM_DATA.cap_amount,
        zero_payroll: data.zero_payroll !== undefined ? data.zero_payroll : DEFAULT_FORM_DATA.zero_payroll,
        modify_issued_documents: data.modify_issued_documents ?? DEFAULT_FORM_DATA.modify_issued_documents,
        biometric_punch_only_inout: data.biometric_punch_only_inout ?? DEFAULT_FORM_DATA.biometric_punch_only_inout,
        is_disabled_clockin: data.is_disabled_clockin ?? DEFAULT_FORM_DATA.is_disabled_clockin,
        grace_times: data.grace_times ?? DEFAULT_FORM_DATA.grace_times,
        deduct_for_contract_emp: data.deduct_for_contract_emp !== undefined ? data.deduct_for_contract_emp : DEFAULT_FORM_DATA.deduct_for_contract_emp,
        is_deduction: parseConfigBool(data.is_deduction, DEFAULT_FORM_DATA.is_deduction),
        salary_deduction: data.salary_deduction || DEFAULT_FORM_DATA.salary_deduction,
        lop_deduction: data.lop_deduction || DEFAULT_FORM_DATA.lop_deduction,
        loc_track_ctl: data.loc_track_ctl ?? DEFAULT_FORM_DATA.loc_track_ctl,
        is_branch_enabled: data.is_branch_enabled ?? DEFAULT_FORM_DATA.is_branch_enabled,
        org_admin_branch_selection: data.org_admin_branch_selection ?? DEFAULT_FORM_DATA.org_admin_branch_selection,
        is_nabh: data.is_nabh ?? DEFAULT_FORM_DATA.is_nabh,
        overtime_enabled: data.overtime_enabled ?? DEFAULT_FORM_DATA.overtime_enabled,
        overtime_multiplier: parseFloat(data.overtime_multiplier ?? DEFAULT_FORM_DATA.overtime_multiplier) || DEFAULT_FORM_DATA.overtime_multiplier,
        overtime_rounding_minutes: parseInt(data.overtime_rounding_minutes ?? DEFAULT_FORM_DATA.overtime_rounding_minutes, 10) || 0,
        overtime_approval_required: data.overtime_approval_required ?? DEFAULT_FORM_DATA.overtime_approval_required,
        overtime_request_min_minutes: parseInt(data.overtime_request_min_minutes ?? DEFAULT_FORM_DATA.overtime_request_min_minutes, 10) || 0,
        short_duty_deduction_enabled: data.short_duty_deduction_enabled ?? DEFAULT_FORM_DATA.short_duty_deduction_enabled,
        short_duty_default_fraction: parseFloat(data.short_duty_default_fraction ?? DEFAULT_FORM_DATA.short_duty_default_fraction) || DEFAULT_FORM_DATA.short_duty_default_fraction,
        short_duty_default_fine: parseFloat(data.short_duty_default_fine ?? DEFAULT_FORM_DATA.short_duty_default_fine) || 0,
        daily_grace_limit: parseInt(data.daily_grace_limit ?? DEFAULT_FORM_DATA.daily_grace_limit, 10) || 0,
        monthly_grace_limit: parseInt(data.monthly_grace_limit ?? DEFAULT_FORM_DATA.monthly_grace_limit, 10) || 0,
        late_penalty_enabled: data.late_penalty_enabled ?? DEFAULT_FORM_DATA.late_penalty_enabled,
        late_fine_slabs: Array.isArray(data.late_fine_slabs)
            ? data.late_fine_slabs
            : DEFAULT_LATE_SLABS,
        tds_enabled: data.tds_enabled ?? DEFAULT_FORM_DATA.tds_enabled,
        tds_default_regime: data.tds_default_regime || DEFAULT_FORM_DATA.tds_default_regime,
        block_permission_on_single_punch: data.block_permission_on_single_punch !== undefined
            ? data.block_permission_on_single_punch
            : DEFAULT_FORM_DATA.block_permission_on_single_punch,
        block_leave_in_probation: data.block_leave_in_probation !== undefined
            ? data.block_leave_in_probation
            : DEFAULT_FORM_DATA.block_leave_in_probation,
        lock_employee_salary_payslip: data.lock_employee_salary_payslip ?? DEFAULT_FORM_DATA.lock_employee_salary_payslip,
        birthday_alerts_enabled: data.birthday_alerts_enabled ?? DEFAULT_FORM_DATA.birthday_alerts_enabled,
        salary_correction_next_cycle: data.salary_correction_next_cycle ?? DEFAULT_FORM_DATA.salary_correction_next_cycle,
        enforce_one_payslip_per_month: data.enforce_one_payslip_per_month ?? DEFAULT_FORM_DATA.enforce_one_payslip_per_month,
        allow_supplement_payroll: data.allow_supplement_payroll ?? DEFAULT_FORM_DATA.allow_supplement_payroll,
        freeze_payroll_config: data.freeze_payroll_config ?? DEFAULT_FORM_DATA.freeze_payroll_config,
        roster_shift_management_enabled: data.roster_shift_management_enabled ?? DEFAULT_FORM_DATA.roster_shift_management_enabled,
        unused_shift_period_days: parseInt(data.unused_shift_period_days ?? DEFAULT_FORM_DATA.unused_shift_period_days, 10)
            || DEFAULT_FORM_DATA.unused_shift_period_days,
        unused_shift_alert_enabled: data.unused_shift_alert_enabled ?? DEFAULT_FORM_DATA.unused_shift_alert_enabled,
    };
}
